import Bugsnag from '@bugsnag/js'
import { v4 as uuidV4 } from 'uuid'
import { getTabId } from 'util/tabId'
import { getVersion } from 'util/version'
import { uniq } from 'util/arrays'
import { LOGOUT_PAGE } from 'constants/pages'
import { MAINTENANCE_PAGE } from 'subapps/maintenance/pages'
import storage from 'util/storage'
import {
  doRefreshIfNecessary,
  waitForAuthRefreshLock,
} from 'ducks/tokens/actions'
import {
  selectAccountSubdomain,
  selectIsRefreshingToken,
  oauthTokenSelector,
} from 'selectors/app'
import { selectAccountSubdomainFromLocation } from 'selectors/location'
import doRedirectToBilling from 'subapps/settings/actions/doRedirectToBilling'
import config from 'config'

// Custom Error class for GraphQL errors that preserves all error details.
// This ensures Bugsnag's unhandledrejection handler can properly process the error
// instead of reporting "unhandledrejection handler received a non-error".
class GraphQLError extends Error {
  constructor(message, details = {}) {
    super(message)
    this.name = details.name || 'GraphQLError'
    // Copy all properties from details (data, errors, requestId, doNotReport, etc.)
    Object.assign(this, details)
    // Capture stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, GraphQLError)
    }
  }
}

// Reports the "instant relogout" decision to Bugsnag so the OAuth/refresh login
// loop (logged in, then bounced straight back to /login) is observable and
// correlatable with the backend oauth_* logs. Severity info; never logs the token.
const reportAuthLogout = (reason, meta = {}) => {
  try {
    const hostname = window?.location?.hostname || ''
    const data = {
      reason,
      subdomain: hostname.split('.')[0],
      path: window?.location?.pathname,
      tokenPresent: Boolean((storage.get('auth') || {}).token),
      ...meta,
    }
    Bugsnag.leaveBreadcrumb('Auth logout (401)', data, 'error')
    Bugsnag.notify(new Error(`Auth logout: ${reason}`), event => {
      // eslint-disable-next-line no-param-reassign
      event.severity = 'info'
      event.addMetadata('authLogout', data)
    })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.debug('Failed to report auth logout', e)
  }
}

// Reports every 401-driven logout and decides whether to dispatch LOGOUT_PAGE.
// IMPORTANT: production data shows the real bounces fire with NO token in storage
// — the token is missing when the bootstrap query (DraftsQuery /
// TicketConversationCountQuery) runs, even right after a successful login — so we
// must NOT gate reporting on tokenPresent; that would hide the actual signal.
// Instead we report everything and tag it: reportAuthLogout records tokenPresent,
// and we add onLoginRoute so the post-logout churn can be filtered out in the
// dashboard (real in-app bounces are onLoginRoute:false). We only return true to
// dispatch LOGOUT_PAGE when inside the app — on a login/logout route there is
// nothing to redirect to and re-dispatching just churns the reload loop. Never
// logs the token.
const shouldAuthLogout = (reason, meta = {}) => {
  const path = window?.location?.pathname || ''
  const onLoginRoute = path.startsWith('/login') || path.startsWith('/logout')
  reportAuthLogout(reason, { ...meta, onLoginRoute })
  return !onLoginRoute
}

// maps the special snowflake payloads that GQL spits at us:
//
//  data: {...},
//  errors:[{
//      message :"{"message":"Request http://api.groovehq.docker/v1/template_categories?page=1 returned 401","status":401}"
//      originalError:"{"message":"Request http://api.groovehq.docker/v1/template_categories?page=1 returned 401","status":401}"
//    }]
//
// into something more sane...
//
//  data: {...},
//  errors:[{
//      message: "Request http://api.groovehq.docker/v1/template_categories?page=1 returned 401",
//      status: '401',
//      originalError:"{"message":"Request http://api.groovehq.docker/v1/template_categories?page=1 returned 401","status":401}"
//   }]
//
// Just to be even more special, GQL will sometimes also spit this format of error at us;
//
// {
//   "errors": [
//     {
//       "message": "Syntax Error GraphQL request (3:20) Unexpected Name \"null\"\n\n2:       query TicketQuery {\n3:         ticket(id: null) {\n                      ^\n4:           \n"
//     }
//   ]
// }

const mapErrors = (json, mode) => {
  return {
    ...json,
    errors: json.errors.map(error => {
      if (mode === 'gqlv2') return error

      const { originalError, message: jsonMessage } = error
      let message = null
      let status = null
      let meta = {}
      try {
        const parsed = JSON.parse(jsonMessage)
        message = parsed.message
        status = parsed.status
        meta = parsed.meta || {}
      } catch (parsingError) {
        // eslint-disable-next-line no-console
        console.error(parsingError)
        message = jsonMessage
        status = 400 // assume its a GQL syntax error
      }

      return {
        originalError,
        message,
        status,
        meta,
      }
    }),
  }
}

function isRefreshing() {
  return selectIsRefreshingToken(app.store.getState())
}

async function refreshIfNecessary() {
  return app.store.dispatch(doRefreshIfNecessary())
}

// Resolve the bearer token for an authenticated request. Prefer localStorage — it
// is written first on a token refresh, so it is the freshest — and fall back to
// the Redux token when storage was momentarily cleared. The cross-subdomain
// handoff (doUpdateAppData) and the auth callback both call localStorage.clear()
// while the app, per Redux, is still authenticated; without this fallback the
// request goes out with no Authorization, the server 401s, and the app misreads
// the race as a logout (the "logged in then instantly kicked out" loop). A
// genuinely revoked token (cleared in another tab/device) leaves storage empty
// and only the stale Redux token to send, which still 401s -> logout, as intended.
function getAuthToken() {
  const stored = (storage.get('auth') || {}).token
  if (stored) return stored
  try {
    return oauthTokenSelector(app.store.getState())
  } catch (e) {
    return undefined
  }
}

function getAccountSubdomain() {
  const state = app.store.getState()
  return (
    selectAccountSubdomain(state) || selectAccountSubdomainFromLocation(state)
  )
}

// Returns a rejected promise on network errors,
// resolves otherwise (caller should inspect response status/payload for
// 40x/50x errors etc)
const post = async (url, headers, body) => {
  const start = new Date().getTime()

  const response = await fetch(url, {
    method: 'POST',
    body,
    mode: 'cors',
    redirect: 'follow',
    headers,
    credentials: 'omit',
  })

  let parsed = null
  let runtime
  let latency
  let jsonParseError = null
  try {
    parsed = await response.json()
    runtime = response.headers.get('x-runtime')
    if (runtime) {
      const elapsed = new Date().getTime() - start
      runtime = parseInt(runtime, 10)
      latency = elapsed - runtime
    }
  } catch (e) {
    runtime = null
    latency = null
    jsonParseError = e
  }
  return {
    runtime,
    latency,
    json: parsed || null,
    status: response?.status,
    jsonParseError,
  }
}

const AUTH_401_RETRIES = 3

const hasAuth401 = error => error?.errors?.find(err => err.status === 401)

const isQueryOperation = query => query.trim().match(/^query\s/)

const shouldRetryAuth401 = (response, query) => {
  if (!isQueryOperation(query)) return false
  if (response.authRetryAttempt >= AUTH_401_RETRIES) return false
  if (getAuthToken() === response.requestAuthToken) return false
  return true
}

const waitForActiveRefreshBeforeAuth401 = async response => {
  await waitForAuthRefreshLock({
    originalToken: response.requestAuthToken,
    subdomain: getAccountSubdomain(),
  })
}

const authRetryExhaustedError = requestId =>
  new GraphQLError('GraphQL auth retry exhausted', {
    requestId,
    doNotReport: true,
  })

// xhr POST request with a linear backoff for failed network requests. Given
// options = { retries: 3, retryDelay: 2000 } then it will retry the request
// in 2s, 4s, 6s. Defaults to 3 retries, at a 1s, 2s and 3s interval.
async function postWithRetry(
  url,
  headers,
  body,
  retries = 3,
  authRetryAttempt = 0
) {
  const retryDelay = 1000

  await refreshIfNecessary()

  return new Promise((resolve, reject) => {
    const wrappedPost = n => {
      // If we are currently refreshing the access token, then we want to
      // reschedule this request for later. This is to prevent race conditions,
      // since as soon as a refreshed access token is used then the previous one
      // becomes invalid.
      const token = getAuthToken()
      const headersWithToken = {
        ...headers,
        ...(token && { Authorization: `Bearer ${token}` }),
      }
      if (isRefreshing()) {
        setTimeout(() => {
          wrappedPost(n)
        }, retryDelay)
        return
      }
      post(url, headersWithToken, body)
        .then(response => {
          Object.defineProperties(response, {
            requestAuthToken: { value: token },
            authRetryAttempt: { value: authRetryAttempt },
          })
          resolve(response)
        })
        .catch(error => {
          if (n <= retries) {
            setTimeout(() => wrappedPost(n + 1), retryDelay * n) // linear
          } else {
            reject(error)
          }
        })
    }
    wrappedPost(1)
  })
}

function getQueryName(query) {
  const stripped = query.replace(/^\s+|\s+$/g, '') // trim
  const match = stripped.match(/^(\w+\s\w+)/)
  if (!match) return stripped.slice(0, 32)
  return match[0]
}

// If the response json has errors (or is null), map to a rejected promise.
const mapGraphQLResponse = (
  finalUrl,
  url,
  headers,
  body,
  query,
  variables,
  requestId
) => response =>
  new Promise((resolve, reject) => {
    let doNotReport = false
    const queryName = getQueryName(query)
    const errorName = `GraphQL ${queryName} call failed`
    const isGQLV2 = url === config.app_graphql_url
    const { json, status, jsonParseError } = response
    let error

    if (json && json.errors) {
      const errorDetails = mapErrors(json, isGQLV2 ? 'gqlv2' : 'default') // returns something our callers can consume
      error = new GraphQLError(errorName, {
        ...errorDetails,
        name: `Unhandled: ${errorName}`,
        requestId,
      })
    } else if (
      isGQLV2 &&
      status === 401 &&
      !window?.location?.pathname?.includes('billing')
    ) {
      return waitForActiveRefreshBeforeAuth401(response)
        .then(() => {
          if (shouldRetryAuth401(response, query)) {
            return postWithRetry(
              finalUrl,
              headers,
              JSON.stringify(body),
              0,
              response.authRetryAttempt + 1
            )
              .then(
                mapGraphQLResponse(
                  finalUrl,
                  url,
                  headers,
                  body,
                  query,
                  variables,
                  requestId
                )
              )
              .then(resolve, reject)
          }
          if (getAuthToken() !== response.requestAuthToken) {
            return reject(authRetryExhaustedError(requestId))
          }
          if (
            shouldAuthLogout('graphql_http_401', {
              queryName,
              requestId,
              httpStatus: status,
            })
          ) {
            app.store.dispatch({ type: LOGOUT_PAGE })
          }
          return resolve(response)
        })
        .catch(reject)
    } else if (!json) {
      if (jsonParseError) {
        // JSON parsing failed - likely network interruption or incomplete response
        error = new Error(
          `Response body could not be parsed: ${jsonParseError.message}`
        )
        error.isJsonParseError = true
        error.jsonParseError = jsonParseError
        error.doNotReport = true
        // Don't report to Bugsnag - this is typically a network/transport issue, not an app bug
        doNotReport = true
        // eslint-disable-next-line no-console
        console.error('GraphQL response JSON parse error:', jsonParseError)
      } else {
        // Genuinely empty response from server - this is unexpected
        error = new Error('500: Request failed (no json)')
      }
    }

    if (error) {
      if (
        error?.errors &&
        (error.errors.every(err => err.status === 404) ||
          error.errors.every(err => err.status === 422) ||
          error.errors.every(
            err => err.extensions?.code === 'RESOURCE_MERGED'
          ) ||
          error.errors.every(
            err => err.extensions?.code === 'RESOURCE_DELETED'
          ) ||
          error.errors.every(err => err.extensions?.code === 'UNAUTHORIZED') ||
          error.errors.every(err => err.extensions?.code === 'NOT_FOUND') ||
          error.errors.every(err => err.extensions?.code === 'TIMEOUT') ||
          error.errors.every(err => err.extensions?.code === 'LOCK_TIMEOUT') ||
          // Timeout errors from GraphQL::Schema::Timeout plugin have message format "Timeout on {FieldPath}"
          // These are already reported by the backend, so we don't need to report them again
          error.errors.every(err => err.message?.startsWith('Timeout on ')))
      ) {
        // There are all considered "handled" errors. This means we dont want to report it to bugsnag
        // as these are valid responses from the api.
        doNotReport = true
      }

      if (hasAuth401(error)) {
        doNotReport = true
        if (isGQLV2) {
          if (json?.data?.billing) {
            app.store.dispatch(doRedirectToBilling())
          } else {
            return waitForActiveRefreshBeforeAuth401(response)
              .then(() => {
                if (shouldRetryAuth401(response, query)) {
                  return postWithRetry(
                    finalUrl,
                    headers,
                    JSON.stringify(body),
                    0,
                    response.authRetryAttempt + 1
                  )
                    .then(
                      mapGraphQLResponse(
                        finalUrl,
                        url,
                        headers,
                        body,
                        query,
                        variables,
                        requestId
                      )
                    )
                    .then(resolve, reject)
                }
                if (getAuthToken() !== response.requestAuthToken) {
                  return reject(authRetryExhaustedError(requestId))
                }
                if (
                  shouldAuthLogout('graphql_error_401', {
                    queryName,
                    requestId,
                    httpStatus: status,
                  })
                ) {
                  app.store.dispatch({ type: LOGOUT_PAGE })
                }
                return reject(error)
              })
              .catch(reject)
          }
        }
      }

      if (error?.errors?.find(err => err.status === 503) && isGQLV2) {
        doNotReport = true
        app.store.dispatch({ type: MAINTENANCE_PAGE })
      }

      // Timeout errors are transient infrastructure issues, not application bugs.
      // The API returns partial data with timeout errors when fields take too long to resolve.
      // These should not be reported to Bugsnag as they're expected under load.
      if (
        error?.errors?.every(
          err =>
            err.message?.startsWith('Timeout on ') ||
            err.message?.includes('Timeout')
        )
      ) {
        doNotReport = true
      }

      if (doNotReport === false) {
        Bugsnag.notify(new Error(errorName), event => {
          // eslint-disable-next-line no-param-reassign
          event.groupingHash = event.errors[0].errorMessage
          // eslint-disable-next-line no-param-reassign
          event.severity = 'error'
          // eslint-disable-next-line no-param-reassign
          event.context = queryName

          event.addMetadata('metaData', {
            meta: {
              query,
              variables,
              requestId,
              status: response.status,
              responseErrors: response.json && response.json.errors, // don't send the actual response, just errors
            },
          })
          event.addMetadata('request', {
            url,
            method: 'POST',
            headers,
            body,
          })
        })
        // we already reported, so don't push it again to Bugsnag
        error.doNotReport = true
      }
      return reject(error)
    }
    // eslint-disable-next-line no-param-reassign
    response.requestId = requestId

    return resolve(response)
  })

const errorIs404 = error => {
  const { errors = [] } = error || {}
  const codes = uniq(errors.map(e => e.status))
  return codes.length === 1 && codes[0] === 404
}

// Returns a function that will execute a graphql query/mutation against the
// configured `url`. Does not do authorization checking or special 401 handling
export const callGraphQL = (url, _token, query, variables, requestId) => {
  const isQuery = query.trim().match(/^query\s/)
  const queryMatches = query.match(/^\s*((query)|(mutation))\s+([^\s(]+)/)
  const queryName = queryMatches && queryMatches[4]
  const retries = isQuery ? 3 : 0 // Only retry queries, not mutations
  const data = { query, _method: 'POST' }
  if (variables) data.variables = variables
  const body = JSON.stringify(data)
  const adHocRequestId = uuidV4()

  const headers = {
    'Content-Type': 'application/json',
    'X-Request-Id': requestId || adHocRequestId,
    'X-Client-Tab-Id': getTabId(),
    'X-Client-App-Version': getVersion(),
  }

  // Split the URL to remove any existing query parameters
  const [baseUrl, existingQuery] = url.split('?')

  // Construct new query string
  const newQueryString = queryName ? `_${queryName}` : ''
  const finalQueryString =
    newQueryString && existingQuery
      ? `${newQueryString}&${existingQuery}`
      : newQueryString || existingQuery || ''

  const finalUrl = finalQueryString ? `${baseUrl}?${finalQueryString}` : baseUrl

  return postWithRetry(finalUrl, headers, body, retries)
    .then(
      mapGraphQLResponse(
        finalUrl,
        url,
        headers,
        data,
        query,
        variables,
        requestId || adHocRequestId
      )
    )
    .catch(error => {
      // log it to make debugging easer since this error results in a redirect
      if (!errorIs404(error)) {
        // eslint-disable-next-line no-console
        console.error(JSON.stringify({ request: body, error }))
      }
      throw error
    })
}

// Main Entrypoint. Returns a function that will execute a graphql
// query/mutation against the configured `url`.
export const graphQLAPI = url => (token, query, variables, requestId) =>
  callGraphQL(url, token, query, variables, requestId)
